import { Chip } from "./Chip";
import { ChipRowFrame } from "./ChipRowFrame";
import type { UiTheme } from "../theme";

// A row of chips where any number can be on: «In which days [D1✓] [D2]
// [D3✓]» (#336, the program editor's copy and add to several days). The
// sibling of ChipChoiceRow, in the same frame: a group of checkboxes instead
// of a radiogroup. Each chip says checked and draws its ✓, never colour only.

export type ChipMultiRowProps<T extends string | number> = {
  theme: UiTheme;
  choices: readonly T[];
  selected: ReadonlySet<T>;
  /** The tapped choice; the owner of the set flips it (toggleSetValue). */
  onToggle: (value: T) => void;
  label: (value: T) => string;
  chipA11yLabel: (value: T) => string;
  groupA11yLabel: string;
  title?: string;
  /** One line that scrolls sideways (see ChipChoiceRow). */
  scroll?: boolean;
  edgeInset?: number;
};

export function ChipMultiRow<T extends string | number>({
  theme,
  choices,
  selected,
  onToggle,
  label,
  chipA11yLabel,
  groupA11yLabel,
  title,
  scroll = false,
  edgeInset = 0,
}: ChipMultiRowProps<T>) {
  return (
    <ChipRowFrame
      theme={theme}
      choices={choices}
      groupRole="group"
      groupA11yLabel={groupA11yLabel}
      title={title}
      scroll={scroll}
      edgeInset={edgeInset}
      follow={null}
      renderChip={(value) => (
        <Chip
          theme={theme}
          role="checkbox"
          prominent
          // Vertical only: horizontal slop would overlap the next chip.
          hitSlop={{ top: 8, bottom: 8 }}
          label={label(value)}
          accessibilityLabel={chipA11yLabel(value)}
          selected={selected.has(value)}
          onPress={() => onToggle(value)}
        />
      )}
    />
  );
}
